import React, { useEffect, useMemo, useState } from "react";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import NavigationHeader from "../../components/ui/NavigationHeader";
import PayableMetricsCard from "./components/PayableMetricsCard";
import PayableFilterBar from "./components/PayableFilterBar";
import PayableAgingChart from "./components/PayableAgingChart";
import SuppliersTable from "./components/SuppliersTable";
import { formatBaseLabel } from "../../utils/baseDisplay";
import { authenticatedFetch } from "../../auth/auth";

const API_BASE_RAW = (import.meta.env?.VITE_API_BASE_URL || "").trim();
const API_BASE = API_BASE_RAW.replace(/\/+$/, "");

const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const parseDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : val;

  const s = String(val).trim();
  if (!s) return null;

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(`${s.slice(0, 10)}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const m1 = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m1) {
    const dd = Number(m1[1]);
    const mm = Number(m1[2]);
    const yy = Number(m1[3]);
    const d = new Date(yy, mm - 1, dd);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toBR = (d) =>
  d
    ? d.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";

const diffDays = (a, b) => {
  if (!a || !b) return NaN;
  const ms = 24 * 60 * 60 * 1000;
  const A = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const B = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.floor((B - A) / ms);
};

const BRL = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fmtBRL = (v) => BRL.format(Number.isFinite(v) ? v : 0);

const toMoneyFromApi = (input) => {
  if (input == null) return 0;

  if (typeof input === "number") {
    if (!Number.isFinite(input)) return 0;
    return input / 100;
  }

  const s0 = String(input).trim();
  if (!s0) return 0;

  if (/^-?\d+$/.test(s0)) return Number(s0) / 100;

  const s = s0
    .replace(/\s/g, "")
    .replace(/[^0-9.,-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const pick = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return null;
};

const pickDeep = (obj, paths) => {
  for (const p of paths) {
    const parts = p.split(".");
    let cur = obj;
    let ok = true;
    for (const part of parts) {
      if (cur && Object.prototype.hasOwnProperty.call(cur, part)) cur = cur[part];
      else {
        ok = false;
        break;
      }
    }
    if (ok && cur !== undefined && cur !== null && String(cur).trim() !== "") return cur;
  }
  return null;
};

const mapStatus = (raw, daysToDue) => {
  const s = String(raw || "").toLowerCase();
  if (s.includes("pago") || s.includes("baixado")) return "paid";
  if (s.includes("parcial")) return "partial";
  if (s.includes("cancel")) return "canceled";
  if (!Number.isNaN(daysToDue) && daysToDue < 0) return "overdue";
  return "pending";
};

const getJSON = async (url) => {
  const res = await authenticatedFetch(url, { headers: { Accept: "application/json" } });

  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (!ct.includes("application/json")) {
    const txt = await res.text();
    throw new Error(`Resposta não-JSON do servidor: ${txt.slice(0, 160)}`);
  }

  const data = await res.json();
  if (res.status === 401 || data?.ok === false) throw new Error(data?.message || "Unauthorized");

  return data;
};

const isProbablySupplierName = (val) => {
  const s = String(val || "").trim();
  if (!s) return false;

  if (/^\d+$/.test(s)) return false;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return false;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return false;
  if (s.length < 3) return false;
  if (!/[A-Za-zÀ-ÿ]/.test(s)) return false;

  const low = s.toLowerCase();
  const banned = [
    "pendente",
    "pago",
    "baixado",
    "cancelado",
    "parcial",
    "vencido",
    "todos",
    "a",
    "b",
    "all",
    "ok",
    "true",
    "false",
  ];
  if (banned.includes(low)) return false;

  return true;
};

const findByKeyRecursive = (obj, keyMatchers, depth = 4) => {
  if (!obj || depth < 0) return null;

  if (Array.isArray(obj)) {
    for (const it of obj) {
      const r = findByKeyRecursive(it, keyMatchers, depth - 1);
      if (r) return r;
    }
    return null;
  }

  if (typeof obj !== "object") return null;

  for (const [k, v] of Object.entries(obj)) {
    const key = String(k || "");
    const match = keyMatchers.some((rx) => rx.test(key));
    if (match) {
      if (typeof v === "string" && isProbablySupplierName(v)) return v.trim();

      if (v && typeof v === "object") {
        const inside =
          pick(v, ["nome", "razaoSocial", "razao_social", "fantasia", "descricao", "desc", "label"]) ||
          pickDeep(v, ["pessoa.nome", "pessoa.razaoSocial", "pessoa.razao_social"]);
        if (inside && isProbablySupplierName(inside)) return String(inside).trim();

        const deep = findByKeyRecursive(v, keyMatchers, depth - 1);
        if (deep) return deep;
      }
    }
  }

  for (const v of Object.values(obj)) {
    if (v && typeof v === "object") {
      const r = findByKeyRecursive(v, keyMatchers, depth - 1);
      if (r) return r;
    }
  }

  return null;
};

const normalizeSupplierName = (raw) => {
  const direct =
    pick(raw, [
      "fornecedor",
      "favorecido",
      "cliente",
      "pessoa",
      "emitente",
      "nomeFornecedor",
      "fornecedorNome",
      "fornecedorRazaoSocial",
      "razaoSocialFornecedor",
      "fornecedor_fantasia",
      "favorecido_nome",
      "nome_favorecido",
      "descricao_favorecido",
      "contato",
    ]) || null;

  const deepKnown =
    pickDeep(raw, [
      "fornecedor.nome",
      "fornecedor.razaoSocial",
      "fornecedor.razao_social",
      "fornecedor.fantasia",
      "fornecedor.descricao",
      "favorecido.nome",
      "favorecido.razaoSocial",
      "favorecido.razao_social",
      "favorecido.fantasia",
      "favorecido.descricao",
      "cliente.nome",
      "cliente.razaoSocial",
      "cliente.razao_social",
      "pessoa.nome",
      "pessoa.razaoSocial",
      "pessoa.razao_social",
      "emitente.nome",
      "emitente.razaoSocial",
      "emitente.razao_social",
      "emitente.fantasia",
      "emitente.descricao",
      "favorecido.pessoa.nome",
      "favorecido.pessoa.razaoSocial",
      "favorecido.pessoa.razao_social",
      "fornecedor.pessoa.nome",
      "fornecedor.pessoa.razaoSocial",
      "fornecedor.pessoa.razao_social",
      "contato.nome",
      "contato.razaoSocial",
      "contato.razao_social",
      "contato.fantasia",
      "contato.descricao",
    ]) || null;

  const candidate1 = String(direct && typeof direct === "object" ? "" : direct || "").trim();
  if (isProbablySupplierName(candidate1)) return candidate1;

  const candidate2 = String(deepKnown || "").trim();
  if (isProbablySupplierName(candidate2)) return candidate2;

  const keyMatchers = [
    /fornecedor/i,
    /favorecid/i,
    /benefici/i,
    /cliente/i,
    /emitente/i,
    /pessoa/i,
    /razao/i,
    /fantasia/i,
    /descricao/i,
    /nome/i,
    /contato/i,
  ];

  const found = findByKeyRecursive(raw, keyMatchers, 4);
  if (found && isProbablySupplierName(found)) return String(found).trim();

  if (raw && typeof raw === "object") {
    const stack = [raw];
    const seen = new Set();

    while (stack.length) {
      const cur = stack.pop();
      if (!cur || typeof cur !== "object") continue;
      if (seen.has(cur)) continue;
      seen.add(cur);

      for (const v of Object.values(cur)) {
        if (typeof v === "string" && isProbablySupplierName(v)) return v.trim();
        if (v && typeof v === "object") stack.push(v);
      }
    }
  }

  return "Não informado";
};

const normalizeItem = (raw) => {
  const supplier = normalizeSupplierName(raw);

  const invoiceNumber =
    pick(raw, [
      "notaFiscal",
      "nf",
      "numeroDocumento",
      "documento",
      "numero",
      "invoiceNumber",
      "numeroNota",
      "numero_nf",
      "numDocumento",
      "codDocumento",
    ]) || String(pick(raw, ["id", "codigo", "codTitulo"]) || "-");

  const category =
    pick(raw, ["categoria", "category", "grupo", "centroCusto", "classificacao", "tipoDespesa", "planoConta"]) ||
    "Não categorizado";

  const valueRaw = pick(raw, ["valor", "amount", "valorTitulo", "valorTotal", "total", "valor_original"]) ?? 0;

  const amount = Math.abs(toMoneyFromApi(valueRaw));
  const signedAmount = -amount;

  const dueDateRaw = pick(raw, ["vencimento", "dueDate", "dtVenc", "dataVencimento", "dt_venc"]);
  const issueDateRaw = pick(raw, ["emissao", "issueDate", "dtEmi", "dataEmissao", "dt_emi"]);

  const dueDate = parseDate(dueDateRaw);
  const issueDate = parseDate(issueDateRaw);

  const today = new Date();
  const daysToDue = dueDate ? diffDays(today, dueDate) : NaN;

  const statusRaw = pick(raw, ["situacao", "status", "situacaoTitulo", "sit", "situacao_titulo"]);
  const status = mapStatus(statusRaw, daysToDue);

  const id =
    String(pick(raw, ["id", "codigo", "codTitulo", "tituloId"]) || "") ||
    `${supplier}-${invoiceNumber}-${dueDateRaw || ""}-${amount}`;

  return {
    id,
    base: raw?.base || raw?.origemBase || "ALL",
    supplier: String(supplier).trim(),
    invoiceNumber: String(invoiceNumber).trim(),
    category: String(category).trim(),
    amount,
    signedAmount,
    status,
    statusRaw: statusRaw || "",
    dueDateISO: dueDate ? ymd(dueDate) : "",
    dueDateBR: dueDate ? toBR(dueDate) : "",
    issueDateBR: issueDate ? toBR(issueDate) : "",
    daysToDue,
    hasReceipt: Boolean(raw?.hasReceipt),
    raw,
  };
};

const inValueRange = (amount, key) => {
  const v = Number.isFinite(amount) ? amount : 0;
  if (key === "0-1000") return v <= 1000;
  if (key === "1000-5000") return v > 1000 && v <= 5000;
  if (key === "5000-10000") return v > 5000 && v <= 10000;
  if (key === "10000+") return v > 10000;
  return true;
};

const matchesAgingPeriod = (daysToDue, periodKey) => {
  if (periodKey === "all") return true;
  if (Number.isNaN(daysToDue)) return false;
  if (daysToDue < 0) return false;

  if (periodKey === "current") return daysToDue >= 0 && daysToDue <= 30;
  if (periodKey === "31-60") return daysToDue >= 31 && daysToDue <= 60;
  if (periodKey === "61-90") return daysToDue >= 61 && daysToDue <= 90;
  if (periodKey === "over90") return daysToDue >= 91;

  return true;
};

const AccountsPayableManagementDashboard = () => {
  const today = new Date();
  const deDefault = ymd(new Date(today.getFullYear(), today.getMonth(), 1));
  const ateDefault = ymd(new Date(today.getFullYear(), today.getMonth() + 3, today.getDate()));

  const [base, setBase] = useState("ALL");
  const [de, setDe] = useState(deDefault);
  const [ate, setAte] = useState(ateDefault);

  const [filters, setFilters] = useState({
    searchTerm: "",
    paymentStatus: "all",
    agingPeriod: "all",
    supplierCategory: "all",
    amountRange: "all",
  });

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rawItems, setRawItems] = useState([]);

  const load = async () => {
    setErr("");

    if (!API_BASE) {
      setErr("Defina VITE_API_BASE_URL apontando para seu backend (ex.: https://api.dibrownie.com.br).");
      return;
    }

    setLoading(true);
    try {
      const qs = new URLSearchParams({ de, ate, base, situacao: "TODOS" }).toString();
      const url = `${API_BASE}/api/pagar?${qs}`;
      const data = await getJSON(url);

      const list =
        (Array.isArray(data?.itens) && data.itens) ||
        (Array.isArray(data?.items) && data.items) ||
        (Array.isArray(data?.data) && data.data) ||
        (Array.isArray(data?.titulos) && data.titulos) ||
        (Array.isArray(data) && data) ||
        [];

      setRawItems(list);
    } catch (e) {
      setErr(e.message || "Falha ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [base, de, ate]);

  const items = useMemo(() => rawItems.map(normalizeItem), [rawItems]);

  const supplierCategories = useMemo(() => {
    const set = new Set(items.map((i) => i.category).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    const q = filters.searchTerm.trim().toLowerCase();

    return items.filter((i) => {
      if (q) {
        const hay = `${i.supplier} ${i.invoiceNumber}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      if (filters.paymentStatus !== "all") {
        if (filters.paymentStatus === "pending" && !(i.status === "pending" || i.status === "partial")) return false;
        if (filters.paymentStatus === "overdue" && i.status !== "overdue") return false;
        if (filters.paymentStatus === "paid" && i.status !== "paid") return false;
      }

      if (!matchesAgingPeriod(i.daysToDue, filters.agingPeriod)) return false;

      if (filters.supplierCategory !== "all" && i.category !== filters.supplierCategory) return false;

      if (filters.amountRange !== "all" && !inValueRange(i.amount, filters.amountRange)) return false;

      return true;
    });
  }, [items, filters]);

  const openItems = useMemo(
    () => filtered.filter((i) => i.status !== "paid" && i.status !== "canceled"),
    [filtered]
  );

  const metrics = useMemo(() => {
    const totalOpen = openItems.reduce((s, i) => s + (Number.isFinite(i.amount) ? i.amount : 0), 0);

    const overdueOpen = openItems.filter((i) => i.status === "overdue");
    const overdueAmount = overdueOpen.reduce((s, i) => s + (Number.isFinite(i.amount) ? i.amount : 0), 0);

    const in30 = openItems.filter((i) => (Number.isNaN(i.daysToDue) ? false : i.daysToDue <= 30));
    const impact30 = in30.reduce((s, i) => s + (Number.isFinite(i.amount) ? i.amount : 0), 0);

    const terms = openItems
      .map((i) => {
        const issue = parseDate(i.raw?.dtEmi || i.raw?.emissao || i.raw?.issueDate || i.raw?.dataEmissao || i.raw?.dt_emi);
        const due = parseDate(i.raw?.dtVenc || i.raw?.vencimento || i.raw?.dueDate || i.raw?.dataVencimento || i.raw?.dt_venc);
        if (!issue || !due) return null;
        return diffDays(issue, due);
      })
      .filter((n) => typeof n === "number" && !Number.isNaN(n) && n >= 0);

    const avgTerm = terms.length ? Math.round(terms.reduce((s, n) => s + n, 0) / terms.length) : 0;

    return {
      totalOpen,
      totalCountOpen: openItems.length,
      overdueAmount,
      overdueCount: overdueOpen.length,
      impact30,
      avgTerm,
    };
  }, [openItems]);

  const aging = useMemo(() => {
    const buckets = {
      due0_30: { amount: 0, count: 0 },
      due31_60: { amount: 0, count: 0 },
      due61_90: { amount: 0, count: 0 },
      due90p: { amount: 0, count: 0 },
    };

    for (const i of openItems) {
      if (Number.isNaN(i.daysToDue)) continue;
      if (i.daysToDue < 0) continue;

      if (i.daysToDue <= 30) {
        buckets.due0_30.amount += i.amount;
        buckets.due0_30.count += 1;
      } else if (i.daysToDue <= 60) {
        buckets.due31_60.amount += i.amount;
        buckets.due31_60.count += 1;
      } else if (i.daysToDue <= 90) {
        buckets.due61_90.amount += i.amount;
        buckets.due61_90.count += 1;
      } else {
        buckets.due90p.amount += i.amount;
        buckets.due90p.count += 1;
      }
    }

    return { totalOpen: metrics.totalOpen, buckets };
  }, [openItems, metrics.totalOpen]);

  const topSuppliers = useMemo(() => {
    const map = new Map();

    for (const i of openItems) {
      const key = i.supplier || "Não informado";
      if (!map.has(key)) map.set(key, { name: key, amount: 0, nextDue: null, overdue: false });

      const r = map.get(key);
      r.amount += Number.isFinite(i.amount) ? i.amount : 0;

      const due = parseDate(i.dueDateISO || i.raw?.dtVenc || i.raw?.vencimento || i.raw?.dueDate || i.raw?.dataVencimento || i.raw?.dt_venc);
      if (due && (!r.nextDue || due < r.nextDue)) r.nextDue = due;

      if (i.status === "overdue") r.overdue = true;
    }

    return Array.from(map.values())
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5)
      .map((s) => ({
        name: s.name,
        amount: s.amount,
        nextDueBR: s.nextDue ? toBR(s.nextDue) : "",
        statusLabel: s.overdue ? "Vencido" : "Pendente",
      }));
  }, [openItems]);

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />

      <main className="max-w-7xl mx-auto px-4 pb-6 pt-24">
        <div className="bg-card rounded-lg border border-border card-shadow p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h1 className="font-heading-semibold text-2xl text-foreground flex items-center space-x-2">
                <Icon name="CreditCard" size={22} />
                <span>Contas a Pagar</span>
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Gestão de pagamentos e análise de fornecedores</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" iconName="BarChart3" iconPosition="left">
                Relatório Aging
              </Button>
              <Button variant="outline" size="sm" iconName="FileText" iconPosition="left">
                Extratos Fornecedores
              </Button>
              <Button variant="default" size="sm" iconName="Plus" iconPosition="left">
                Pagamento Manual
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border card-shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Icon name="SlidersHorizontal" size={18} className="text-muted-foreground" />
              <span className="font-body-medium text-card-foreground">Controle de Período e Empresa</span>
            </div>

            <div className="flex items-center gap-2">
              {loading && <span className="text-sm text-muted-foreground">Carregando...</span>}
              {err && <span className="text-sm text-error">{err}</span>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div>
              <label className="text-xs text-muted-foreground">Empresa</label>
              <select
                className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={base}
                onChange={(e) => setBase(e.target.value)}
              >
                <option value="ALL">{formatBaseLabel("ALL")}</option>
                <option value="A">{formatBaseLabel("A")}</option>
                <option value="B">{formatBaseLabel("B")}</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-muted-foreground">De</label>
              <input
                className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                type="date"
                value={de}
                onChange={(e) => setDe(e.target.value)}
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Até</label>
              <input
                className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                type="date"
                value={ate}
                onChange={(e) => setAte(e.target.value)}
              />
            </div>

            <div className="md:col-span-2 flex items-end justify-end gap-2">
              <Button variant="outline" size="sm" iconName="RefreshCcw" iconPosition="left" onClick={load} disabled={loading}>
                {loading ? "Atualizando..." : "Atualizar"}
              </Button>
            </div>
          </div>
        </div>

        <PayableFilterBar
          onFiltersChange={setFilters}
          onRefresh={load}
          isLoading={loading}
          supplierCategories={supplierCategories}
        />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 my-6">
          <PayableMetricsCard
            title="Total a Pagar"
            value={fmtBRL(metrics.totalOpen)}
            trend="up"
            trendValue=""
            icon="Wallet"
            status="warning"
            subtitle={`${metrics.totalCountOpen} títulos`}
          />
          <PayableMetricsCard
            title="Prazo Médio Pagamento"
            value={`${metrics.avgTerm} dias`}
            trend=""
            trendValue=""
            icon="Calendar"
            status="success"
            subtitle="média considerando vencimentos"
          />
          <PayableMetricsCard
            title="Contas Vencidas"
            value={fmtBRL(metrics.overdueAmount)}
            trend="up"
            trendValue=""
            icon="AlertTriangle"
            status="error"
            subtitle={`${metrics.overdueCount} faturas em atraso`}
          />
          <PayableMetricsCard
            title="Impacto (30 dias)"
            value={fmtBRL(metrics.impact30)}
            trend=""
            trendValue=""
            icon="TrendingDown"
            status="warning"
            subtitle="próximos 30 dias (inclui vencidos)"
          />
        </div>

        <PayableAgingChart aging={aging} topSuppliers={topSuppliers} />

        <div className="my-6">
          <SuppliersTable items={filtered} />
        </div>
      </main>
    </div>
  );
};

export default AccountsPayableManagementDashboard;
